'use client'

import { useEffect, useId, useRef, type ReactNode } from 'react'
import { useTranslations } from 'next-intl'

import { ArrowLeft } from 'lucide-react'

import type { MoodSectionData, TagShelf } from '@/lib/blog/moods'
import type { PostCardData } from '@/components/blog/mood-post-card'
import { MoodPostGrid } from '@/components/blog/mood-post-grid'

import { AllStoriesGrid } from './all-stories-grid'
import {
    ALL_STORIES_COLLECTION,
    backToBlog,
    useActiveCollectionSlug,
    useActiveMoodSlug,
} from './mood-filter'

interface BlogHomeContentProps {
    /** The full blog homepage (all sections, incl. the mood tiles). */
    children: ReactNode
    /** Featured + non-featured moods with their post listings — resolved server-side. */
    moods: MoodSectionData
    /**
     * The season and editorial shelves. They live in their own sections but open
     * the same in-place view, so filter resolution has to see every tag — not
     * just the mood section's.
     */
    otherTags?: TagShelf[]
    /** Every post, trimmed to card fields, newest first — the all-stories view's data. */
    allStories: PostCardData[]
}

/**
 * The blog home body switch (blog-moods FR-8, RD-15; blog FR-13b): with no
 * filter param the full homepage (`children`) renders; when a mood is active
 * the homepage is **cleared** and only that mood's post listing shows — a
 * "← Back to blog" link, the mood label, and the {@link MoodPostGrid} (or a
 * friendly empty state). `?collection=all` clears it the same way to the
 * {@link AllStoriesGrid} — the whole corpus grouped by month. A URL naming
 * both renders the mood view (mood is checked first).
 *
 * The active filter is read from the URL (`useActiveMoodSlug` /
 * `useActiveCollectionSlug`) whose server snapshots are `null`, so the server
 * HTML is always the full homepage regardless of the params — keeping `/blog`
 * a single static, canonical surface (no filter variant is indexed). Selection
 * is driven by `history.pushState` (see `./mood-filter`), so the browser Back
 * button returns to the homepage.
 */
export const BlogHomeContent = ({
    children,
    moods,
    otherTags = [],
    allStories,
}: BlogHomeContentProps) => {
    const t = useTranslations('blogHome')
    const activeSlug = useActiveMoodSlug()
    const activeCollection = useActiveCollectionSlug()
    const headingId = useId()
    const headingRef = useRef<HTMLHeadingElement | null>(null)

    const activeMood = activeSlug
        ? ([...moods.featured, ...moods.nonFeatured, ...otherTags].find(
              (mood) => mood.slug === activeSlug,
          ) ?? null)
        : null
    const showAllStories = !activeMood && activeCollection === ALL_STORIES_COLLECTION
    // One key per open view (mood slug, or the collection) — `null` = homepage.
    const activeViewKey = activeMood?.slug ?? (showAllStories ? ALL_STORIES_COLLECTION : null)

    // The filter view replaces the whole homepage like a client-side route
    // change; move focus to its heading so keyboard/SR users land in — and
    // hear — the new context instead of being stranded on <body>. Re-runs per
    // opened view.
    useEffect(() => {
        if (activeViewKey !== null) {
            headingRef.current?.focus()
        }
    }, [activeViewKey])

    if (!activeMood && !showAllStories) {
        return <>{children}</>
    }

    return (
        <section
            aria-labelledby={headingId}
            className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-10 md:py-14"
        >
            <button
                type="button"
                onClick={backToBlog}
                className="inline-flex items-center gap-2 text-[15px] font-medium text-text-secondary hover:text-text-primary"
            >
                <ArrowLeft aria-hidden="true" className="size-4" />
                {t('wanderByMood.back')}
            </button>
            <h1
                id={headingId}
                ref={headingRef}
                tabIndex={-1}
                className="mt-4 mb-8 text-3xl font-bold tracking-tight text-text-primary outline-none md:text-4xl"
            >
                {activeMood ? activeMood.label : t('allStories.heading')}
            </h1>
            {activeMood ? (
                activeMood.posts.length === 0 ? (
                    <p className="text-text-secondary">{t('wanderByMood.empty')}</p>
                ) : (
                    <MoodPostGrid key={activeMood.slug} posts={activeMood.posts} />
                )
            ) : (
                <AllStoriesGrid posts={allStories} />
            )}
        </section>
    )
}
